import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Instagram } from "lucide-react";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { INSTAGRAM_HANDLE, instagramUrl } from "@/lib/social";
import { ABOUT_BIO, ABOUT_CREDENTIAL, ABOUT_PRINCIPLES, aboutWorkLine } from "@/data/about-contact";
import studio from "@/assets/dr-sara-studio.jpg.asset.json";
import street from "@/assets/dr-sara-street-circle.jpg.asset.json";
import { ConsultBand } from "@/components/public/consult-band";
import { Section } from "@/components/ds/section";
import { eyebrow } from "@/components/ds/styles";
import { FiguresBlock } from "@/components/ds/figures-block";
import { CredentialsSection } from "@/components/about/credentials-section";
import { blogServicesQ } from "@/features/blog/queries";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const band = "mx-3 rounded-ds-2xl lg:mx-auto lg:max-w-[76rem]";

export const Route = createFileRoute("/_public/about")({
  loader: ({ context }) => context.queryClient.ensureQueryData(blogServicesQ()),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  head: () => ({
    meta: [
      { title: "من نحن — د. سارة النحّاس، استشارية تربوية ونفسية" },
      { name: "description", content: "د. سارة النحّاس، استشارية تربوية ونفسية ودكتوراه في التربية من جامعة دمشق: المنهج، المبادئ الثلاثة، والمؤهلات والشهادات كاملة." },
      { property: "og:title", content: "من نحن — د. سارة النحّاس، استشارية تربوية ونفسية" },
      { property: "og:description", content: "د. سارة النحّاس، استشارية تربوية ونفسية ودكتوراه في التربية من جامعة دمشق: المنهج، المبادئ الثلاثة، والمؤهلات والشهادات كاملة." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { property: "og:site_name", content: "د. سارة النحّاس" },
      ...ogImageMeta(),
      ...canonicalMeta("/about"),
    ],
    links: [canonicalLink("/about")],
  }),
  component: About,
});

function About() {
  const services = Route.useLoaderData();
  const { queryClient } = Route.useRouteContext();
  if (queryClient.getQueryData(blogServicesQ().queryKey) === undefined) {
    queryClient.setQueryData(blogServicesQ().queryKey, services);
  }
  const { data: settings } = useQuery(publicSiteSettingsQuery());
  return (
    <div dir="rtl">
      <Section tone="cream" inner="mx-auto max-w-[76rem] px-5 pb-14 pt-8 lg:grid lg:grid-cols-2 lg:items-center lg:gap-x-12 lg:pb-20 lg:pt-14">
        <div className="lg:col-start-1 lg:row-start-1 lg:self-end">
          <p className={eyebrow}>من نحن</p>
          <h1 className="mt-2 font-display text-[42px] font-extrabold leading-tight text-text-primary lg:text-[58px]">
            أنا <span className="mark">سارة النحّاس</span>.
          </h1>
        </div>
        <div className="relative mx-auto mt-6 w-full max-w-[260px] overflow-hidden rounded-b-ds-xl rounded-t-[220px] bg-maroon-100 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:max-w-[400px]">
          <img src={studio.url} alt="د. سارة النحّاس" width={720} height={1080} loading="eager" fetchPriority="high" decoding="async" className="aspect-[4/5] w-full object-cover object-center" />
        </div>
        <div className="lg:col-start-1 lg:row-start-2 lg:self-start">
          <p className="mt-5 font-display text-[14px] font-bold text-text-secondary">{ABOUT_CREDENTIAL}</p>
          <p className="mt-3 text-[17px] leading-[1.85] text-text-primary">{aboutWorkLine(settings?.clinic_address_ar)}</p>
          <p className="mt-3 text-[17px] leading-[1.85] text-text-primary">{ABOUT_BIO}</p>
          <p dir="ltr" lang="en" className="mt-3 text-start text-[17px] leading-[1.85] text-text-primary">
            Dr. Sara Alnahhas — Parenting and educational consultant, PhD in Education (Damascus University). Online consultations in Arabic from any country, or at the clinic in Abu Rummaneh, Damascus.
          </p>
        </div>
      </Section>

      <Section tone="white" className="py-14 lg:py-20">
      <div className={band + " bg-blue-100 px-6 py-10 lg:px-12"}>
        <blockquote className="font-display text-[28px] font-extrabold leading-snug text-text-primary">
          «السلوك ليس مشكلة.. <span className="mark">السلوك رسالة</span>»
        </blockquote>
        <p className="mt-3 text-[14px] text-blue-700">د. سارة النحّاس</p>
      </div>
      </Section>

      <Section tone="cream" className="py-14 lg:py-20">
      <div className="mx-auto max-w-[76rem] lg:grid lg:grid-cols-2 lg:items-start lg:gap-10 lg:px-3">
        <section className="px-5 lg:px-2">
          <p className={eyebrow}>ثلاثة مبادئ</p>
          <h2 className="mt-1 font-display text-[30px] font-extrabold text-text-primary">اللي بيرجع كل شي إله</h2>
          <ol className="mt-4 divide-y divide-hairline-subtle">
            {ABOUT_PRINCIPLES.map((p, i) => (
              <li key={p.title} className="flex gap-4 py-4">
                <span className="font-display text-[40px] font-extrabold leading-none text-amber-800">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display text-[17px] font-bold text-text-primary">{p.title}</h3>
                  <p className="mt-1 text-[17px] leading-[1.85] text-text-secondary">{p.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-11 px-5 lg:mt-0 lg:px-2">
          <p className={eyebrow}>الأمان قبل الانضباط</p>
          <h2 className="mt-1 font-display text-[30px] font-extrabold text-text-primary">أرقام من الطريق</h2>
          <div className="mt-5">
            <FiguresBlock compact />
          </div>
        </section>
      </div>
      </Section>

      <Section tone="white" id="credentials" className="scroll-mt-20">
        <div className="reveal"><CredentialsSection /></div>
      </Section>

      <Section tone="cream" className="py-14 lg:py-20">
      <div className="reveal mx-3 lg:mx-auto lg:max-w-[76rem]">
        <a
          href={instagramUrl(settings?.instagram_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="lift flex min-h-11 items-center gap-4 rounded-ds-xl bg-maroon-100 p-5 transition-colors duration-ds-fast hover:bg-maroon-200"
        >
          <span className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-maroon-500 text-cream-100">
            <Instagram className="h-6 w-6" strokeWidth={1.75} aria-hidden />
          </span>
          <span>
            <span className="block font-display text-[17px] font-bold text-text-primary">متابعة د. سارة على إنستغرام</span>
            <span className="mt-0.5 block text-[14px] text-text-secondary">
              <span dir="ltr">{INSTAGRAM_HANDLE}</span> · <bdi dir="ltr">1M+</bdi> متابع
            </span>
          </span>
        </a>
      </div>
      </Section>

      <Section tone="white" className="py-14 lg:py-20">
        <div className="reveal">
        <ConsultBand
          photo={{ src: street.url, alt: "د. سارة النحّاس", width: 440, height: 440 }}
          title="بدكم تحكوا مع د. سارة عن طفلكم بالذات؟"
          text="45 دقيقة، أونلاين أو بالعيادة، وخطة عملية لبيتكم. اليوم والساعة بنتفق عليهم على واتساب."
          showPackage
          footer={<Link to="/bousala" className="inline-flex min-h-11 items-center font-display font-bold underline-offset-4 hover:underline">عمرك بين 15 و35؟ بدء البوصلة مجاناً ←</Link>}
        />
        </div>
      </Section>
    </div>
  );
}
